import React, { useState } from "react";
import { Label } from "flowbite-react";
import { useMutation } from "react-query";
import { HiOutlineX } from "react-icons/hi";

import Title from "../../../components/Title";
import SubTitle from "../../../components/SubTitle";
import Button from "../../../components/Button";
import Notification from "../../../components/Notification";
import ToastNotification from "../../../components/ToastNotification";
import { ToastType } from "../../../typesData/ToastNotificationTypes";
import { getRoles, manageRole } from "../../../api/roles/actions";
import { MANAGEABLE_ROLES, Role, RoleAction } from "../../../api/roles/types";
import { useActorEmail } from "../../../hooks/useActorEmail";
import { roleManagementStrings, formatMessage } from "./strings";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Banner = { type: ToastType; message: string };

export default function RoleManagement() {
  const actorEmail = useActorEmail();
  const [emailInput, setEmailInput] = useState("");
  const [searchedEmail, setSearchedEmail] = useState("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [pendingRole, setPendingRole] = useState<Role | null>(null);

  const isValidEmail = EMAIL_REGEX.test(emailInput);

  const searchMutation = useMutation(() => getRoles(emailInput), {
    onSuccess: (data) => {
      setRoles(data);
      setSearchedEmail(emailInput);
      setBanner(null);
    },
    onError: () => {
      setSearchedEmail("");
      setBanner({
        type: "FAILURE",
        message: formatMessage(roleManagementStrings.messages.loadError, {
          email: emailInput,
        }),
      });
    },
  });

  const manageMutation = useMutation(
    ({ role, action }: { role: Role; action: RoleAction }) =>
      manageRole(searchedEmail, role, action, actorEmail),
    {
      onSuccess: (data, { role, action }) => {
        setRoles(data);
        const message = formatMessage(
          action === "grant"
            ? roleManagementStrings.messages.grantSuccess
            : roleManagementStrings.messages.revokeSuccess,
          { role: roleManagementStrings.roleLabels[role], email: searchedEmail }
        );
        setBanner({ type: "SUCCESS", message });
        ToastNotification({ type: "success", message });
      },
      onError: (_error, { role, action }) => {
        const message = formatMessage(
          action === "grant"
            ? roleManagementStrings.messages.grantError
            : roleManagementStrings.messages.revokeError,
          { role: roleManagementStrings.roleLabels[role], email: searchedEmail }
        );
        setBanner({ type: "FAILURE", message });
        ToastNotification({ type: "failure", message });
      },
      onSettled: () => setPendingRole(null),
    }
  );

  const isBusy = searchMutation.isLoading || manageMutation.isLoading;

  const handleSearch = () => {
    if (!isValidEmail || isBusy) {
      return;
    }
    searchMutation.mutate();
  };

  const handleManage = (role: Role, action: RoleAction) => {
    if (!actorEmail) {
      setBanner({
        type: "FAILURE",
        message: roleManagementStrings.messages.missingActor,
      });
      return;
    }
    setPendingRole(role);
    manageMutation.mutate({ role, action });
  };

  const clearInput = () => {
    setEmailInput("");
    setSearchedEmail("");
    setRoles([]);
    setBanner(null);
  };

  return (
    <div className="pt-8 min-h-screen w-full">
      <div className="mb-8">
        <Title>{roleManagementStrings.pageTitle}</Title>
      </div>

      <div className="px-4 py-4 shadow-md bg-white rounded-lg">
        <div className="px-4 py-2 mb-2">
          <SubTitle>{roleManagementStrings.search.sectionTitle}</SubTitle>
        </div>
        <hr />
        {banner && (
          <div className="px-4">
            <Notification type={banner.type} message={banner.message} />
          </div>
        )}
        <div className="px-4 py-2">
          <div className="mb-4">
            <Label
              htmlFor="role-email"
              color="grey"
              value={roleManagementStrings.search.inputLabel}
            />
          </div>
          <div className="flex">
            <input
              id="role-email"
              data-testid="role-email-input"
              type="text"
              className="w-1/3 focus:outline-none px-5 py-[10px] bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder-zinc-400 max-[1300px]:w-[300px]"
              value={emailInput}
              onChange={(event) => setEmailInput(event.target.value)}
              placeholder={roleManagementStrings.search.inputPlaceholder}
            />
            {emailInput && (
              <button
                type="button"
                aria-label="clear"
                onClick={clearInput}
                className="relative inset-y-0 right-0 -ml-6 pr-3 flex items-center"
              >
                <HiOutlineX className="h-5 w-5 text-gray-500" />
              </button>
            )}
          </div>
          <div className="mt-4">
            <Button
              variant="PRIMARY"
              disabled={!isValidEmail || isBusy}
              isProcessing={searchMutation.isLoading}
              onClick={handleSearch}
            >
              {roleManagementStrings.search.submitButton}
            </Button>
          </div>
        </div>
      </div>

      {searchedEmail && (
        <div
          className="px-6 my-6 py-6 shadow-md bg-white rounded-lg"
          data-testid="role-results"
        >
          <div className="mb-4">
            <SubTitle>
              {formatMessage(roleManagementStrings.results.heading, {
                email: searchedEmail,
              })}
            </SubTitle>
          </div>
          {roles.length === 0 && (
            <p className="text-neutral-500 text-sm mb-4">
              {roleManagementStrings.results.noRoles}
            </p>
          )}
          <div className="flex flex-col gap-3">
            {MANAGEABLE_ROLES.map((role) => {
              const isGranted = roles.includes(role);
              const action: RoleAction = isGranted ? "revoke" : "grant";
              return (
                <div
                  key={role}
                  data-testid={`role-row-${role}`}
                  className="flex items-center justify-between border border-gray-100 rounded-lg p-4 max-w-[480px]"
                >
                  <div className="flex flex-col">
                    <span className="text-neutral-800 text-sm font-semibold">
                      {roleManagementStrings.roleLabels[role]}
                    </span>
                    <span className="text-neutral-500 text-xs">
                      {isGranted
                        ? roleManagementStrings.results.granted
                        : roleManagementStrings.results.notGranted}
                    </span>
                  </div>
                  <Button
                    variant={isGranted ? "DANGER" : "PRIMARY"}
                    disabled={isBusy || !actorEmail}
                    isProcessing={
                      manageMutation.isLoading && pendingRole === role
                    }
                    onClick={() => handleManage(role, action)}
                  >
                    {isGranted
                      ? roleManagementStrings.results.revoke
                      : roleManagementStrings.results.grant}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
